import { Calendar, CheckCircle, Clock, Target, Trophy, Users } from 'lucide-react';
import { Stage } from 'modules/client/tournaments/domain/types';
import getRoundTypeLabel from 'modules/client/tournaments/domain/utils/get-round-type-label';
import React from 'react';

interface StageSelectorProps {
  stages: Stage[];
  activeStage: number;
  onStageChange: (stageId: number) => void;
}

const StageSelector: React.FC<StageSelectorProps> = ({ stages, activeStage, onStageChange }) => {
  const getStageIcon = (type: string, isPlayoff: boolean) => {
    if (isPlayoff) return <Target size={16} />;
    switch (type) {
      case 'group':
        return <Users size={16} />;
      case 'knockout':
        return <Trophy size={16} />;
      case 'single-round':
      case 'double-round':
        return <Calendar size={16} />;
      default:
        return <Calendar size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'upcoming':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={12} />;
      case 'active':
        return <div className="h-3 w-3 animate-pulse rounded-full bg-blue-600"></div>;
      case 'upcoming':
        return <Clock size={12} />;
      default:
        return <Clock size={12} />;
    }
  };

  return (
    <div className="mb-6 overflow-hidden rounded-lg bg-white shadow-md">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 text-white">
        <h2 className="text-lg font-bold">Этапы турнира</h2>
        <p className="text-sm text-indigo-100">Выберите этап для просмотра</p>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stages.map((stage) => (
            <button
              key={stage.id}
              onClick={() => onStageChange(stage.id)}
              className={`rounded-lg border-2 p-4 text-left transition-all hover:shadow-md ${
                activeStage === stage.id
                  ? 'border-indigo-500 bg-indigo-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-indigo-300'
              }`}
            >
              <div className="mb-2 flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  {getStageIcon(stage.type, stage.isPlayoff)}
                  <h3 className={`font-bold ${activeStage === stage.id ? 'text-indigo-900' : 'text-gray-900'}`}>{stage.name}</h3>
                </div>
                <div
                  className={`flex items-center space-x-1 rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(stage.status)}`}
                >
                  {getStatusIcon(stage.status)}
                  <span className="capitalize">
                    {stage.status === 'active' ? 'Активный' : stage.status === 'completed' ? 'Завершён' : 'Предстоящий'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Тип:</span>
                  <span className={`font-medium ${activeStage === stage.id ? 'text-indigo-700' : 'text-gray-900'}`}>
                    {getRoundTypeLabel(stage.type)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Команд:</span>
                  <span className={`font-medium ${activeStage === stage.id ? 'text-indigo-700' : 'text-gray-900'}`}>
                    {stage.teams.length}
                  </span>
                </div>

                {stage.isPlayoff && (
                  <div className="flex items-center space-x-1 rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-700">
                    <Trophy size={12} />
                    <span>Плей-офф</span>
                  </div>
                )}
              </div>

              {stage.description && <p className="mt-2 line-clamp-2 text-xs text-gray-600">{stage.description}</p>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StageSelector;
